import { ShieldAlert, List, Users, Ban, Eye, Package, BadgeCheck, Clock, Flag, Check, Coins, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatPrice, resolveImageUrl } from "@/lib/api";

const STATUS_STYLES = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  sold: "bg-slate-100 text-slate-600 border-slate-200",
  removed: "bg-red-50 text-red-700 border-red-200",
  pending_payment: "bg-amber-50 text-amber-700 border-amber-200",
  draft: "bg-slate-50 text-slate-500 border-slate-200",
};

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500">{label}</span>
        <Icon size={16} className={color} />
      </div>
      <p className={`font-heading text-2xl font-bold mt-1 ${color}`}>{value ?? "0"}</p>
    </div>
  );
}

export function OverviewTab({ ctx }) {
  const { stats } = ctx;
  return (
    <div data-testid="admin-content-overview" className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <StatCard label="Total listings" value={stats?.total_listings} icon={Package} color="text-slate-900" />
      <StatCard label="Active" value={stats?.active} icon={BadgeCheck} color="text-emerald-600" />
      <StatCard label="Sold" value={stats?.sold} icon={BadgeCheck} color="text-slate-500" />
      <StatCard label="Awaiting payment" value={stats?.pending} icon={Clock} color="text-amber-600" />
      <StatCard label="Removed" value={stats?.removed} icon={Ban} color="text-red-600" />
      <StatCard label="Drafts" value={stats?.drafts} icon={List} color="text-slate-500" />
      <StatCard label="Registered users" value={stats?.users} icon={Users} color="text-slate-900" />
      <StatCard label="Open reports" value={stats?.open_reports} icon={Flag} color="text-red-600" />
    </div>
  );
}

export function ModerationTab({ ctx }) {
  const { reports, navigate, removeListing, resolveReport } = ctx;
  return (
    <div data-testid="admin-content-moderation" className="space-y-4">
      <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><ShieldAlert size={18} className="text-red-500" /> Reported ads ({reports.length})</h2>
      {reports.length === 0 ? (
        <p className="text-slate-400 text-sm" data-testid="moderation-empty">Nothing flagged right now. The backlog is clear.</p>
      ) : (
        <div className="space-y-3">
          {reports.map((r) => (
            <div key={r.id} data-testid={`report-row-${r.id}`} className="bg-white border border-slate-200 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <img src={resolveImageUrl(r.listing_image || "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=200&q=80")} alt={r.listing_title || "Reported listing"} className="w-16 h-16 rounded object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 truncate flex items-center gap-2">
                    {r.listing_title}
                    {r.listing_status === "removed" && <span className="text-xs bg-red-100 text-red-700 rounded px-1.5 py-0.5">Removed</span>}
                    {r.listing_status === "gone" && <span className="text-xs bg-slate-200 text-slate-600 rounded px-1.5 py-0.5">Deleted</span>}
                  </p>
                  <p className="text-sm text-red-600 font-medium">{r.reason}</p>
                  {r.details && <p className="text-xs text-slate-500 truncate">{r.details}</p>}
                </div>
              </div>
              <div className="flex flex-wrap gap-1 sm:justify-end">
                {r.listing_status !== "gone" && (
                  <>
                    <Button size="sm" variant="outline" data-testid={`report-view-${r.id}`} onClick={() => navigate(`/listing/${r.listing_id}`)}>View</Button>
                    <Button size="sm" variant="outline" data-testid={`report-remove-${r.id}`} onClick={() => removeListing(r.listing_id)} className="text-red-600 border-red-200"><Ban size={15} className="mr-1" /> Remove ad</Button>
                  </>
                )}
                <Button size="sm" variant="outline" data-testid={`report-resolve-${r.id}`} onClick={() => resolveReport(r.id)} className="text-emerald-700 border-emerald-200"><Check size={15} className="mr-1" /> Resolve</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ListingsTab({ ctx }) {
  const { listings, navigate, removeListing } = ctx;
  return (
    <div data-testid="admin-content-listings" className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200"><h2 className="font-heading font-semibold text-slate-900">All listings ({listings.length})</h2></div>
      <div className="divide-y divide-slate-100">
        {listings.map((l) => (
          <div key={l.id} data-testid={`admin-listing-${l.id}`} className="p-3 flex items-center gap-3">
            <img src={resolveImageUrl(l.image || "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=200&q=80")} alt={l.title || "Listing"} className="w-12 h-12 rounded object-cover shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-slate-900 truncate">{l.title}</p>
              <p className="text-xs text-slate-500 truncate">{l.category} · {l.county} · {l.seller_name} · <Eye size={11} className="inline" /> {l.views}</p>
            </div>
            <p className="text-sm font-semibold text-emerald-700 hidden sm:block w-24 text-right">{formatPrice(l.price, l.category)}</p>
            <span className={`text-xs border rounded px-2 py-0.5 shrink-0 ${STATUS_STYLES[l.status] || "bg-slate-50 text-slate-500 border-slate-200"}`}>{l.status}</span>
            <div className="flex gap-1 shrink-0">
              <Button size="sm" variant="outline" data-testid={`admin-view-${l.id}`} onClick={() => navigate(`/listing/${l.id}`)}>View</Button>
              {l.status !== "removed" && <Button size="sm" variant="outline" data-testid={`admin-remove-${l.id}`} onClick={() => removeListing(l.id)} className="text-red-600 border-red-200"><Ban size={14} /></Button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function UsersTab({ ctx }) {
  const { users, currentUserId, onAddCredits, onToggleRole } = ctx;
  return (
    <div data-testid="admin-content-users" className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200"><h2 className="font-heading font-semibold text-slate-900">Registered users ({users.length})</h2></div>
      <div className="divide-y divide-slate-100">
        {users.map((u) => {
          const isAdmin = u.role === "admin";
          const isSelf = u.id === currentUserId;
          return (
            <div key={u.id} data-testid={`admin-user-${u.id}`} className="p-3 flex items-center gap-3 flex-wrap sm:flex-nowrap">
              <span className="grid place-items-center w-9 h-9 rounded-full bg-slate-100 text-slate-500 shrink-0"><Users size={16} /></span>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 truncate flex items-center gap-2">{u.name}
                  {isAdmin && <span className="text-xs bg-slate-900 text-white rounded px-1.5 py-0.5">Admin</span>}
                  {isSelf && <span className="text-xs bg-emerald-100 text-emerald-700 rounded px-1.5 py-0.5">You</span>}
                </p>
                <p className="text-xs text-slate-500 truncate">{u.email}</p>
              </div>
              <p className="text-xs text-slate-500 shrink-0 w-14 text-right hidden sm:block">{u.ad_count} ads</p>
              <p className="text-sm font-semibold text-emerald-700 shrink-0 w-20 text-right" data-testid={`admin-user-balance-${u.id}`}>€{(u.credit_balance ?? 0).toFixed(2)}</p>
              <div className="flex gap-1 shrink-0">
                <Button size="sm" variant="outline" data-testid={`admin-add-credits-${u.id}`}
                  onClick={() => onAddCredits(u)} className="text-emerald-700 border-emerald-200">
                  <Coins size={14} className="mr-1" /> Add credits
                </Button>
                <Button size="sm" variant="outline" data-testid={`admin-toggle-role-${u.id}`}
                  onClick={() => onToggleRole(u)} disabled={isSelf}
                  className={isAdmin ? "text-red-600 border-red-200" : "text-slate-700"}>
                  <Shield size={14} className="mr-1" /> {isAdmin ? "Revoke admin" : "Make admin"}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export const ADMIN_PANELS = { overview: OverviewTab, moderation: ModerationTab, listings: ListingsTab, users: UsersTab };
